import React, { useRef } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import classNames from 'classnames';

import NamedLink from '../NamedLink/NamedLink';
import { BRANDS } from '../../config/brandRegistry';
import { normalizeBrandValue } from '../../util/brandAvailability';
import BrandPlaceholder from './BrandPlaceholder';
import css from './BrandCarousel.module.css';

const SCROLL_STEP = 320;

const BrandTileContent = ({ brand, isActive }) => {
  const useBadge = brand.isPlaceholder;
  return (
    <>
      <div
        className={classNames(css.logoContainer, {
          [css.logoContainerDark]: brand.tile === 'dark',
          [css.logoContainerActive]: isActive,
        })}
      >
        {useBadge ? (
          <BrandPlaceholder name={brand.name} className={css.logo} />
        ) : (
          <img src={brand.logo} alt={brand.name} className={css.logo} loading="lazy" />
        )}
      </div>
      <span className={css.brandLabel}>{brand.name}</span>
    </>
  );
};

/**
 * Horizontal, scroll-snapping carousel of brand logos. Each tile links into a
 * brand-filtered listing view (pub_brand=<name>).
 *
 * Reusable across surfaces:
 *  - Default: renders NamedLinks to the search page (`/s`).
 *  - Pass `onSelectBrand` to drive an in-page filter instead (e.g. StorePage,
 *    or LandingPageCustom in iframe mode where the URL push must be controlled).
 */
const BrandCarousel = props => {
  const {
    className,
    rootClassName,
    brands,
    activeBrandName,
    onSelectBrand,
    title,
    linkName,
    availableBrandValues,
  } = props;

  const scrollerRef = useRef(null);
  const items = brands && brands.length ? brands : BRANDS;
  const visibleItems = Array.isArray(availableBrandValues)
    ? items.filter(brand => availableBrandValues.includes(normalizeBrandValue(brand.value || brand.name)))
    : items;

  if (!visibleItems.length) return null;

  const scrollBy = direction => {
    const node = scrollerRef.current;
    if (node) node.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' });
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <section className={classes}>
      {title ? <h2 className={css.title}>{title}</h2> : null}

      <div className={css.viewport}>
        <button
          type="button"
          className={classNames(css.arrow, css.arrowLeft)}
          aria-label="Scroll brands left"
          onClick={() => scrollBy(-1)}
        >
          ‹
        </button>

        <div className={css.scroller} ref={scrollerRef}>
          {visibleItems.map(brand => {
            // pub_brand carries the canonical lowercase value, not the label.
            const brandValue = brand.value || String(brand.name).toLowerCase();
            const isActive = !!activeBrandName && activeBrandName === brandValue;
            const itemClasses = classNames(css.brandItem, { [css.brandItemActive]: isActive });

            if (onSelectBrand) {
              return (
                <button
                  type="button"
                  key={brand.slug}
                  className={itemClasses}
                  aria-pressed={isActive}
                  onClick={() => onSelectBrand(isActive ? null : brandValue)}
                >
                  <BrandTileContent brand={brand} isActive={isActive} />
                </button>
              );
            }

            return (
              <NamedLink
                key={brand.slug}
                name={linkName}
                to={{ search: `pub_brand=${encodeURIComponent(brandValue)}` }}
                className={itemClasses}
              >
                <BrandTileContent brand={brand} isActive={isActive} />
              </NamedLink>
            );
          })}
        </div>

        <button
          type="button"
          className={classNames(css.arrow, css.arrowRight)}
          aria-label="Scroll brands right"
          onClick={() => scrollBy(1)}
        >
          ›
        </button>
      </div>
    </section>
  );
};

BrandCarousel.defaultProps = {
  className: null,
  rootClassName: null,
  brands: null,
  activeBrandName: null,
  onSelectBrand: null,
  title: null,
  linkName: 'SearchPage',
  availableBrandValues: null,
};

BrandCarousel.propTypes = {
  className: string,
  rootClassName: string,
  brands: arrayOf(
    shape({
      name: string.isRequired,
      value: string,
      slug: string.isRequired,
      logo: string,
      tile: string,
      isPlaceholder: bool,
    })
  ),
  activeBrandName: string,
  onSelectBrand: func,
  title: string,
  linkName: string,
  availableBrandValues: arrayOf(string),
};

export default BrandCarousel;
